/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * EmailVerificationPage
 *
 * React page responsible for verifying a user's email through tokenized links.
 * Features:
 * - Extracts `token` and `email` from query parameters
 * - Sends verification request to `/arcee/user/verify-email/` using React Query
 * - Displays ProgressBarDialog during verification
 * - Renders success UI with themed styling on verified emails
 * - Handles invalid/missing tokens, server errors, and malformed responses
 * - Uses AlertDialog for all error states and redirects appropriately
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AlertDialog from '../components/common/AlertDialog';
import ProgressBarDialog from '../components/common/ProgressBar';
import Button from '../components/Button';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { useQuery } from '@tanstack/react-query';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

function useQueryParams() {
  return new URLSearchParams(useLocation().search);
}

export default function EmailVerificationPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const query = useQueryParams();
  const token = query.get('token');
  const email = query.get('email');

  const queryResult = useQuery({
    queryKey: ['verifyEmail', token, email],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall('/arcee/user/verify-email/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email }),
        signal,
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        const error = new Error('Verification failed');
        error.info = errData;
        throw error;
      }

      return resp.json().catch(() => ({}));
    },
    enabled: !!token && !!email,
    retry: 0,
    staleTime: 0,
    cacheTime: 2 * 60 * 1000,
  });

  const { isLoading, isError, error, isSuccess } = queryResult;

  if (!token || !email) {
    return (
      <AlertDialog
        open={true}
        type="error"
        title="Invalid Link"
        message="Verification link is missing required parameters."
        okLabel="Close"
        showCancel={false}
        onOk={() => navigate('/')}
        mini_mode={false}
      />
    );
  }

  if (isLoading) {
    return <ProgressBarDialog open={true} text_message="Verifying your email..." />;
  }

  if (isSuccess) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#f6f8fa',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 20,
        }}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: theme.cardRadius,
            padding: 40,
            maxWidth: 420,
            width: '90vw',
            boxShadow: '0 2px 12px rgba(70,48,125,0.11)',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              backgroundColor: 'green',
              borderRadius: '50%',
              width: 90,
              height: 90,
              margin: '0 auto 20px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <i className="bi bi-check-lg" style={{ color: 'white', fontSize: 52 }} />
          </div>
          <h2
            style={{
              color: theme.primary,
              fontWeight: 'bold',
              marginBottom: 16,
            }}
          >
            Success
          </h2>
          <h6 style={{ marginBottom: 8 }}>Your email has been verified succesfully.</h6>
          <p style={{ marginBottom: 32 }}>You may now log in.</p>
          <Button mode="bordered" onClick={() => navigate('/sign-in')}>
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <AlertDialog
      open={true}
      type="error"
      title={error?.message || 'Verification Failed'}
      message={(error && error.info && error.info.error) || 'An error occurred during verification.'}
      okLabel="Close"
      showCancel={false}
      onOk={() => navigate('/')}
      mini_mode={false}
    />
  );
}
